// src/store/userActions.ts
import { createAsyncThunk } from '@reduxjs/toolkit';
import authServiceApiInstance from '../../../config/apiConfig'; // Import the configured axios instance
import axios from 'axios';

// Types for User and Response
interface User {
    UserId: number;
    Username: string;
    DisplayName: string;
    Email: string;
    mobile: number;
    Source: string;
    PasswordHash: string;
    PasswordSalt: string;
    CreatedAt: string;
    ModifiedAt: string;
    IsActive: number;
    owner_id: number;
    role_id: number;
    fy_id: number;
    login_otp_authenticate_id: number;
    image: string;
    mobile_flags: string;
    mobile_code: string;
    enc_pass: string;
    active_deactive: string;
    path: string;
    fyName: string;
}

interface UserResponse {
    status: string;
    message: string;
    data: User[];
}

// Create User action
export const createUser = createAsyncThunk(
    'user/createUser',
    async (userData: any, { rejectWithValue }) => {
        try {
            const response = await authServiceApiInstance.post('/users', userData);
            return response.data;
        } catch (error: any) {
            console.log("error", error)
            if (axios.isAxiosError(error)) {
                return rejectWithValue(error.response?.data || 'Error creating user');
            }
            return rejectWithValue('Error creating user');
        }
    }
);

// Get all Users action
export const getUsers = createAsyncThunk<UserResponse, void, { rejectValue: string }>(
    'user/getUsers',
    async (_, { rejectWithValue }) => {
        try {
            const response = await authServiceApiInstance.get('/users');
            return response.data;
        } catch (error: unknown) {
            if (error instanceof Error) {
                return rejectWithValue(error.message || 'Error fetching users');
            }
            return rejectWithValue('Error fetching users');
        }
    }
);

// Get User by ID action
export const getUserById = createAsyncThunk<User, number, { rejectValue: string }>(
    'user/getUserById',
    async (userId: number, { rejectWithValue }) => {
        try {
            const response = await authServiceApiInstance.get(`/users/${userId}`);
            return response.data;
        } catch (error: unknown) {
            if (error instanceof Error) {
                return rejectWithValue(error.message || 'Error fetching user');
            }
            return rejectWithValue('Error fetching user');
        }
    }
);

// Update User action
export const updateUser = createAsyncThunk(
    'user/updateUser',
    async ({ userId, userData }: { userId: number; userData: User }, { rejectWithValue }) => {
        try {
            const formData = new FormData();

            // Append user data to the FormData object
            formData.append('name', userData.DisplayName);
            formData.append('mobile', userData.mobile.toString());

            if (userData.role_id) {
                formData.append('role_id', userData.role_id.toString());
            }
            if (userData.IsActive !== undefined && userData.IsActive !== null) {
                formData.append('IsActive', userData.IsActive.toString());
            }
            // Append image if available
            if (userData.image) {
                formData.append('image', userData.image);
            }

            const response = await authServiceApiInstance.put(`/users/${userId}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error: unknown) {
            if (error instanceof Error) {
                return rejectWithValue(error || 'Error updating user');
            }
            return rejectWithValue('Error updating user');
        }
    }
);
// Delete User action
export const deleteUser = createAsyncThunk(
    'user/deleteUser',
    async (userId: number, { rejectWithValue }) => {
        try {
            const response = await authServiceApiInstance.delete(`/users/${userId}`);
            return response.data;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(
                    error.response?.data?.message || 'Error deleting user'
                );
            }
            return rejectWithValue('Error deleting user');
        }
    }
);

export const updatePassword = createAsyncThunk<
    any,
    { userId: number; oldPassword: string; newPassword: string },
    { rejectValue: string }>(
        'user/updatePassword',
        async ({ userId, oldPassword, newPassword }, { rejectWithValue }) => {
            try {
                const response = await authServiceApiInstance.put(`/users/${userId}/update-password`, {
                    oldPassword,
                    newPassword,
                });
                return response.data;
            } catch (error: any) {
                return rejectWithValue(error.response.data);
            }
        }
    );

export const createUserPermission = createAsyncThunk(
    'user/createUserPermission',
    async (permissionData: { UserId: number; PermissionKey: string; PermissionValues: any[]; }, { rejectWithValue }) => {
        try {
            const response = await authServiceApiInstance.post('/users/permissions', permissionData);
            return response.data;
        } catch (error: unknown) {
            if (error instanceof Error) {
                return rejectWithValue(error.message || 'Error creating user permission');
            }
            return rejectWithValue('Error creating user permission');
        }
    }
);

export const updateUserPermission = createAsyncThunk(
    'user/updateUserPermission',
    async ({ userPermissionId, permissionData }: { userPermissionId: number; permissionData: { PermissionKey: string; PermissionValues: any[]; } }, { rejectWithValue }) => {
        try {
            const response = await authServiceApiInstance.put(`/users/permissions/${userPermissionId}`, permissionData);
            return response.data;
        } catch (error: unknown) {
            if (error instanceof Error) {
                return rejectWithValue(error.message || 'Error updating user permission');
            }
            return rejectWithValue('Error updating user permission');
        }
    }
);

// Delete User Permission
export const deleteUserPermission = createAsyncThunk(
    'user/deleteUserPermission',
    async (userPermissionId: number, { rejectWithValue }) => {
        try {
            const response = await authServiceApiInstance.delete(`/user-permissions/${userPermissionId}`);
            return response.data;
        } catch (error: unknown) {
            if (error instanceof Error) {
                return rejectWithValue(error.message || 'Error deleting user permission');
            }
            return rejectWithValue('Error deleting user permission');
        }
    }
);

// Fetch User Permissions by UserId
export const fetchUserPermissionsByUserId = createAsyncThunk(
    'user/fetchUserPermissionsByUserId',
    async (userId: number, { rejectWithValue }) => {
        try {
            const response = await authServiceApiInstance.get(`/users/${userId}/permissions`);
            return response.data;
        } catch (error: unknown) {
            if (error instanceof Error) {
                return rejectWithValue(error.message || 'Error fetching user permissions');
            }
            return rejectWithValue('Error fetching user permissions');
        }
    }
);
